import { useEffect, useMemo, useState } from "react";
import { useBotInstances } from "../api/hooks/useBotInstances";
import { usePanicClose, useToggleTrading } from "../api/hooks/useControl";
import { useOverview } from "../api/hooks/useOverview";
import { BotInstanceSelector } from "../components/BotInstanceSelector/BotInstanceSelector";
import { HoldButton } from "../components/HoldButton/HoldButton";
import type { ControlTarget } from "../api/types";
import { formatPnl } from "../utils/format";
import { computeWindow } from "../utils/period";

export function Control() {
  const { data: instancesData } = useBotInstances();
  // Only reads open_positions below, which isn't window-scoped at all (see
  // overview.py's own comment) — this page doesn't care WHICH window, just
  // needs a valid one to satisfy the API. Computed once at mount (empty
  // deps), not on every render — a fresh Date.now() every render would
  // otherwise mint a new queryKey (and therefore a refetch) constantly.
  const window_ = useMemo(() => computeWindow("d7", 0, Date.now()), []);
  const { data: overview } = useOverview(window_.from.toISOString(), window_.to.toISOString());
  const toggleTrading = useToggleTrading();
  const panicClose = usePanicClose();

  const [target, setTarget] = useState<ControlTarget>("all");
  const [panicDone, setPanicDone] = useState(false);

  const instances = instancesData?.instances ?? [];
  const selectedInstance = target === "all" ? null : instances.find((i) => i.bot_instance_id === target) ?? null;
  const tradingEnabled = target === "all" ? instances.some((i) => !i.trading_disabled) : !selectedInstance?.trading_disabled;

  useEffect(() => {
    // Clear the "closed" confirmation banner if the operator switches target.
    setPanicDone(false);
  }, [target]);

  const handleToggleTrading = () => {
    toggleTrading.mutate({
      bot_instance_id: target,
      disabled: tradingEnabled,
      reason: "operator toggle",
    });
  };

  const handlePanicConfirm = () => {
    setPanicDone(true);
    panicClose.mutate({ bot_instance_id: target });
  };

  // ALL open positions in scope for the current target, not just one — an
  // earlier version read a single overview.open_position (whichever ONE
  // trade happened to be picked server-side) and hardcoded positionsToClose
  // to 1 whenever target==="all", regardless of how many bots actually had
  // a position open. Confirmed live as a real bug: with 3 bots each holding
  // an open position, the panic-close confirmation showed "1 позиция" for
  // "close all" when 3 were actually about to close.
  const allPositions = overview?.open_positions ?? [];
  const targetedPositions = target === "all" ? allPositions : allPositions.filter((p) => p.bot_instance_id === target);
  const positionsToClose = targetedPositions.length;
  const estimatedPnl = targetedPositions.reduce((sum, p) => sum + parseFloat(p.unrealized_pnl), 0);

  return (
    <div style={{ padding: "26px 28px 44px", display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start", maxWidth: 1080 }}>
      <section style={{ flex: "1 1 380px", minWidth: 0, borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
        <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--line)", font: "600 14.5px 'Golos Text',sans-serif" }}>
          Цель управления
        </div>
        <div style={{ padding: 18 }}>
          <BotInstanceSelector instances={instances} value={target} onChange={setTarget} />
        </div>
      </section>

      <section style={{ flex: "1 1 380px", minWidth: 0, borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
        <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--line)", font: "600 14.5px 'Golos Text',sans-serif" }}>
          Управление торговлей
        </div>
        <div style={{ padding: 18 }}>
          <button
            type="button"
            onClick={handleToggleTrading}
            disabled={toggleTrading.isPending}
            style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: 0, border: 0, background: "transparent", color: "var(--fg)", cursor: "pointer", textAlign: "left" }}
          >
            <span
              style={{
                width: 46,
                height: 26,
                borderRadius: 14,
                flex: "none",
                padding: 3,
                display: "flex",
                justifyContent: tradingEnabled ? "flex-end" : "flex-start",
                background: tradingEnabled ? "var(--long)" : "var(--line2)",
                transition: "background 160ms var(--ease)",
              }}
            >
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--onfill)", boxShadow: "0 1px 3px oklch(0.2 0 0 / .28)" }} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ font: "600 14px 'Golos Text',sans-serif" }}>{tradingEnabled ? "Торговля разрешена" : "Торговля запрещена"}</span>
              <span style={{ font: "400 12px 'Golos Text',sans-serif", color: "var(--fg3)" }}>
                Бот перестанет открывать новые позиции, существующие останутся
              </span>
            </span>
          </button>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 16,
              padding: "11px 14px",
              borderRadius: 11,
              background: tradingEnabled ? "var(--longsoft)" : "var(--warnsoft)",
              border: "1px solid var(--line)",
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: tradingEnabled ? "var(--long)" : "var(--warn)" }} />
            <span style={{ font: "500 12.5px 'Golos Text',sans-serif", color: tradingEnabled ? "var(--long)" : "var(--warn)" }}>
              {tradingEnabled ? "Бот работает в штатном режиме" : "Новые позиции не открываются"}
            </span>
          </div>
        </div>
      </section>

      <section style={{ flex: "1 1 380px", minWidth: 0, borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
        <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--short)", transform: "rotate(45deg)" }} />
          <span style={{ font: "600 13.5px 'Golos Text',sans-serif", color: "var(--short)" }}>Тревожная кнопка</span>
        </div>
        <div style={{ padding: 18 }}>
          <p style={{ margin: "0 0 4px", font: "400 13px/1.55 'Golos Text',sans-serif", color: "var(--fg2)" }}>
            Рыночное закрытие всех открытых позиций {selectedInstance ? `на ${selectedInstance.display_name}` : "по выбранной цели"} на Hyperliquid.
            Действие необратимо.
          </p>
          <div style={{ display: "flex", gap: 16, margin: "14px 0", padding: "12px 14px", borderRadius: 11, background: "var(--surface2)" }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ font: "400 11px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Будет закрыто</span>
              <span style={{ font: "700 15px 'JetBrains Mono',monospace" }}>{positionsToClose} позиция(й)</span>
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ font: "400 11px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Оценка P&L</span>
              <span style={{ font: "700 15px 'JetBrains Mono',monospace", color: "var(--short)" }}>
                {positionsToClose > 0 ? `${formatPnl(estimatedPnl)} USDC` : "—"}
              </span>
            </span>
          </div>

          <HoldButton
            onConfirm={handlePanicConfirm}
            holdMs={900}
            idleLabel="УДЕРЖИВАЙТЕ, ЧТОБЫ ЗАКРЫТЬ ВСЁ"
            doneLabel="КОМАНДА ОТПРАВЛЕНА"
            done={panicDone}
            disabled={positionsToClose === 0}
          />

          {panicDone && (
            <div style={{ marginTop: 12, padding: "11px 14px", borderRadius: 11, background: "var(--longsoft)", border: "1px solid var(--line)", font: "500 12.5px 'Golos Text',sans-serif", color: "var(--long)" }}>
              Команда на закрытие позиций отправлена. Бот выполнит её на ближайшем цикле опроса.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
